import { useState } from 'react';
import { Card, CardBody, Table, Button, Badge, Alert, Spinner } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useFlagged, useApproveFlagged, useConfirmFlagged } from './useNexusMailReview';

/**
 * Spam/Phishing-Review-Queue (#310). Plattformweite Liste markierter Mails (nur
 * Metadaten, cross-tenant). Der Operator bereinigt Falsch-Positive (Approve →
 * entstuft + Absender gelernt, #301) oder bestätigt die Einstufung (Confirm →
 * Junk-Move + Reputation). Backend: nexus-Proxy #309 → mail /v1/admin/flagged*.
 */
function fmt(s) {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE');
}

// Einstufung → sichtbares Badge + kind für Approve/Confirm (spam vs phishing).
function classify(m) {
  if (m.spam) return { kind: 'spam', badge: <Badge color="warning">Spam{m.spamLevel ? ` · ${m.spamLevel}` : ''}</Badge> };
  const color = m.phishingLevel === 'high' ? 'danger' : 'info';
  return { kind: 'phishing', badge: <Badge color={color}>Phishing · {m.phishingLevel || '—'}</Badge> };
}

const LEVELS = [
  { key: 'all', label: 'Alle' },
  { key: 'high', label: 'Phishing hoch' },
  { key: 'medium', label: 'Phishing mittel' },
  { key: 'spam', label: 'Spam' },
];

export function NexusMailReviewPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const [level, setLevel] = useState('all');
  const q = useFlagged(level);
  const approve = useApproveFlagged();
  const confirm = useConfirmFlagged();

  const items = q.data?.items ?? [];
  const busy = approve.isPending || confirm.isPending;

  return (
    <div>
      <PageHeader title="Nexus · Mail-Review" subtitle="Spam/Phishing-Review-Queue (#310) — Falsch-Positive bereinigen, Einstufung bestätigen" />
      <Alert color="info" className="small">
        Plattformweite Liste erkannter verdächtiger Mails (nur Metadaten, kein Mailtext).
        <strong> Freigeben</strong> = Falsch-Positiv (entstuft, Absender wird als „gut" gelernt).
        <strong> Bestätigen</strong> = als Spam/Phishing halten (Junk-Move + Reputation). Beides speist die Lernschleife.
      </Alert>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="btn-group btn-group-sm" role="group">
          {LEVELS.map((l) => (
            <Button key={l.key} size="sm" color="secondary" outline={level !== l.key}
              onClick={() => setLevel(l.key)}>{l.label}</Button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">{items.length} Mails</span>
          <Button size="sm" color="secondary" outline disabled={q.isFetching} onClick={() => q.refetch()}>
            {q.isFetching ? 'Aktualisiere…' : 'Aktualisieren'}
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="p-4 text-center"><Spinner size="sm" /> Lade markierte Mails…</div>
          ) : q.isError ? (
            <Alert color="danger" className="m-3 small">Konnte Liste nicht laden: {String(q.error?.message || q.error)}</Alert>
          ) : items.length === 0 ? (
            <div className="p-4 text-center text-muted">Keine markierten Mails in dieser Ansicht.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle small">
              <thead>
                <tr>
                  <th>Einstufung</th>
                  <th>Absender</th>
                  <th>Betreff</th>
                  <th>Postfach</th>
                  <th>Empfangen</th>
                  <th className="text-end">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => {
                  const c = classify(m);
                  return (
                    <tr key={m.id}>
                      <td>{c.badge}</td>
                      <td>
                        <div>{m.from?.name || '—'}</div>
                        <code className="small text-muted">{m.from?.address || '—'}</code>
                      </td>
                      <td style={{ maxWidth: 320 }} className="text-truncate" title={m.subject || ''}>{m.subject || '—'}</td>
                      <td className="text-muted"><code className="small">{m.mailbox || '—'}</code></td>
                      <td className="text-muted">{fmt(m.receivedAt)}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" color="success" outline className="me-1" disabled={!canWrite || busy}
                          onClick={() => approve.mutate({ id: m.id, kind: c.kind })}>
                          Freigeben
                        </Button>
                        <Button size="sm" color="danger" disabled={!canWrite || busy}
                          onClick={() => confirm.mutate({ id: m.id, kind: c.kind })}>
                          Bestätigen
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
